import {
  deleteCloudDocument,
  getCloudDocument,
  listCloudDocuments,
  migrateLocalDocumentsToCloud,
  mirrorCloudDocumentsToLocal,
  upsertCloudDocument,
} from './cloudDocuments';
import {
  deleteCloudGeneration,
  deleteCloudReference,
  listCloudGenerations,
  persistApprovedReferenceToCloud,
  persistGenerationToCloud,
} from './cloudGenerations';
import { downloadStorageFile, getCloudProjectBundle, listCloudProjects } from './cloudProjects';
import { writesStayLocal } from './localOnly';
import { storagePathForExport } from './picturePaths';
import { auth } from '../firebase';

// Hosting closes /api after 60 seconds. A generate run is longer than that,
// so production calls the Cloud Run service directly (its timeout is 300s).
const CLOUD_API_ORIGIN = 'https://api-3nxbt4z3aa-uc.a.run.app';

function longRequestUrl(path) {
  if (typeof path !== 'string') return path;
  if (/^https?:\/\//.test(path)) return path;
  if (typeof window === 'undefined') return path;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return path;
  return `${CLOUD_API_ORIGIN}${path}`;
}

function apiUrl(path) {
  return longRequestUrl(path);
}

function responseText(body) {
  return String(body || '')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 400);
}

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function jsonFetch(url, options = {}) {
  const { headers: extraHeaders, ...rest } = options;
  const res = await fetch(apiUrl(url), {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(extraHeaders || {}),
      ...(await authHeaders()),
    },
    cache: 'no-store',
  });
  const raw = await res.text();
  let data = null;
  const trimmed = raw.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      data = trimmed ? JSON.parse(trimmed) : {};
    } catch {
      data = null;
    }
  }
  if (data && typeof data === 'object') {
    if (!res.ok) {
      const err = new Error(data.error || data.message || res.statusText || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }
  const text = responseText(raw);
  const err = new Error(text ? `HTTP ${res.status}: ${text}` : `HTTP ${res.status} ${res.statusText || ''}`.trim());
  err.status = res.status;
  throw err;
}

/** Live site reads Firestore and Storage. Localhost reads the local API. */
export function shouldPreferCloudCatalog() {
  return !writesStayLocal();
}

/**
 * Localhost does not copy anything up to Firebase. Production stays the
 * source of truth until a change is made on the live site.
 */
export function shouldMirrorProjectToCloud() {
  return false;
}

export function getMeta() {
  return jsonFetch('/api/meta');
}

export async function listProjects() {
  if (shouldPreferCloudCatalog()) {
    const projects = await listCloudProjects();
    return { projects, source: 'firebase' };
  }
  return jsonFetch('/api/projects');
}

export function listSeries() {
  return jsonFetch('/api/series');
}

export function createSeries(title) {
  return jsonFetch('/api/series', { method: 'POST', body: JSON.stringify({ title }) });
}

export function getSeries(seriesId) {
  return jsonFetch(`/api/series/${encodeURIComponent(seriesId)}`);
}

export function attachBookToSeries(seriesId, projectId, volume) {
  return jsonFetch(`/api/series/${encodeURIComponent(seriesId)}/books`, {
    method: 'POST',
    body: JSON.stringify({ projectId, volume }),
  });
}

export function addSeriesCharacters(seriesId, projectId, characterIds) {
  return jsonFetch(`/api/series/${encodeURIComponent(seriesId)}/characters`, {
    method: 'POST',
    body: JSON.stringify({ projectId, characterIds }),
  });
}

export function useSeriesCharacter(seriesId, projectId, seriesCharacterId) {
  return jsonFetch(`/api/series/${encodeURIComponent(seriesId)}/use`, {
    method: 'POST',
    body: JSON.stringify({ projectId, seriesCharacterId }),
  });
}

export function restoreShelfLocks(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/shelf-locks`, {
    method: 'POST',
    body: '{}',
  });
}

async function planDocsFetch(url) {
  const headers = {};
  const user = auth.currentUser;
  if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;
  const res = await fetch(apiUrl(url), { headers, cache: 'no-store' });
  const text = await res.text();
  if (!res.ok) {
    let message = text;
    try {
      message = JSON.parse(text).error || message;
    } catch {
      /* body is the file */
    }
    const err = new Error(message || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return text;
}

export async function listPlanDocs() {
  const text = await planDocsFetch('/api/plan-docs');
  return JSON.parse(text);
}

export function fetchPlanDoc(docPath, format) {
  const query = new URLSearchParams({ path: docPath, format: format || 'raw' });
  return planDocsFetch(`/api/plan-docs/file?${query}`);
}

export async function listArchivedProjects() {
  if (shouldPreferCloudCatalog()) {
    const projects = await listCloudProjects({ includeArchived: true });
    return { projects, source: 'firebase' };
  }
  return jsonFetch('/api/projects/archived');
}

export async function getProject(projectId) {
  const data = shouldPreferCloudCatalog()
    ? await getCloudProjectBundle(projectId)
    : await jsonFetch(`/api/projects/${encodeURIComponent(projectId)}`);
  if (!data?.project?.id) {
    const err = new Error('Project not found');
    err.status = 404;
    throw err;
  }
  return data;
}

/** Create project — requires bible seed: { title, bible: { markdown } | structured } */
export function createProject(body) {
  return jsonFetch('/api/projects', { method: 'POST', body: JSON.stringify(body) });
}

export function updateProject(projectId, body) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function ingestBible(projectId, body) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/bible`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function exportBibleChanges(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/bible-changes`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

function listDocumentsLocal(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/documents`);
}

function getDocumentLocal(projectId, documentId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}`
  );
}

function uploadDocumentLocal(projectId, body) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/documents`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

function deleteDocumentLocal(projectId, documentId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}`,
    { method: 'DELETE' }
  );
}

async function writeThroughLocal(projectId, body) {
  try {
    await uploadDocumentLocal(projectId, body);
  } catch {
    // Local API is a cache for scan/seed; Firebase is source of truth.
  }
}

/**
 * List Project Documents from Firebase (durable). Migrates any leftover local-disk
 * .md files into Firestore/Storage once, then returns cloud metadata.
 */
export async function listDocuments(projectId) {
  try {
    await migrateLocalDocumentsToCloud(projectId, {
      fetchLocalList: listDocumentsLocal,
      fetchLocalDoc: getDocumentLocal,
    });
  } catch {
    // Migration is best-effort; listing cloud still proceeds when signed in.
  }

  try {
    const documents = await listCloudDocuments(projectId);
    const seen = new Set(documents.map((doc) => String(doc.id || doc.name || '').toLowerCase()));
    try {
      const local = await listDocumentsLocal(projectId);
      for (const doc of local.documents || []) {
        const key = String(doc.id || doc.name || '').toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        documents.push(doc);
      }
    } catch {
      // Local API can be down. The cloud list still stands.
    }
    documents.sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
    return { documents, source: 'firebase' };
  } catch (e) {
    // Fallback to local disk if signed out / rules not deployed yet.
    const local = await listDocumentsLocal(projectId);
    return { documents: local.documents || [], source: 'local', warning: e.message };
  }
}

/** Load one document’s markdown from Firebase Storage. */
export async function getDocument(projectId, documentId) {
  try {
    const document = await getCloudDocument(projectId, documentId);
    if (document) return { document, source: 'firebase' };
  } catch (e) {
    if (e?.code === 'AUTH_REQUIRED') throw e;
  }
  const local = await getDocumentLocal(projectId, documentId);
  return { ...local, source: 'local' };
}

/** Upload/save .md. Localhost writes the local API only. */
export async function uploadDocument(projectId, body) {
  if (writesStayLocal()) {
    const local = await uploadDocumentLocal(projectId, body || {});
    return { ...local, source: 'local' };
  }
  const document = await upsertCloudDocument(projectId, body || {});
  await writeThroughLocal(projectId, {
    name: document.name,
    markdown: document.markdown,
  });
  return { document, source: 'firebase' };
}

/**
 * Delete a project document.
 * Prefer Firebase when signed in; fall back to local disk when auth is missing
 * or the cloud object is already gone (same pattern as listDocuments).
 *
 * Local filenames may still be Mixed_Case.md while cloud ids are lowercased —
 * try both the raw id and the safe (lowercase) name on disk. If neither store
 * has the doc, treat as already removed (idempotent).
 */
export async function deleteDocument(projectId, documentId) {
  const tryLocalDelete = async () => {
    try {
      return await deleteDocumentLocal(projectId, documentId);
    } catch (localErr) {
      const safeId = String(documentId || '')
        .split(/[/\\]/)
        .pop()
        .trim()
        .toLowerCase();
      if (safeId && safeId !== documentId) {
        try {
          return await deleteDocumentLocal(projectId, safeId);
        } catch {
          // fall through
        }
      }
      throw localErr;
    }
  };

  if (writesStayLocal()) return tryLocalDelete();

  try {
    const result = await deleteCloudDocument(projectId, documentId);
    try {
      await tryLocalDelete();
    } catch {
      // Local mirror is best-effort.
    }
    return result;
  } catch (e) {
    if (e?.code === 'AUTH_REQUIRED' || e?.code === 'NOT_FOUND') {
      try {
        return await tryLocalDelete();
      } catch (localErr) {
        // Already gone from both stores — succeed so the UI can refresh.
        if (localErr?.status === 404 || localErr?.data?.code === 'NOT_FOUND') {
          return {
            deleted: true,
            alreadyGone: true,
            id: documentId,
            projectId,
          };
        }
        throw localErr;
      }
    }
    throw e;
  }
}

/** Mirror cloud docs to local disk, then run Express heuristic scan. */
export async function scanDocuments(projectId) {
  try {
    await mirrorCloudDocumentsToLocal(projectId, {
      upsertLocal: uploadDocumentLocal,
      fetchLocalList: listDocumentsLocal,
    });
  } catch {
    // Scan may still work from whatever is already on disk.
  }
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/documents-scan`);
}

export function applyDocumentAssignments(projectId, proposals) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/documents-assign`, {
    method: 'POST',
    body: JSON.stringify({ proposals }),
  });
}

export function archiveProject(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/archive`, {
    method: 'POST',
    body: '{}',
  });
}

export function restoreProject(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/restore`, {
    method: 'POST',
    body: '{}',
  });
}

/** Hard-delete filesystem content — only after archive. */
export function deleteProjectFully(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}`, {
    method: 'DELETE',
  });
}

export function getStructure(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/structure`);
}

function structureItemPath(projectId, section, itemId, suffix = '') {
  const base = `/api/projects/${encodeURIComponent(projectId)}/${section}/${encodeURIComponent(itemId)}`;
  return suffix ? `${base}/${suffix}` : base;
}

export function getTreatment(projectId, chapterId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/treatments/${encodeURIComponent(chapterId)}`
  );
}

export function refreshTreatment(projectId, chapterId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/treatments/${encodeURIComponent(chapterId)}/refresh`,
    { method: 'POST', body: '{}' }
  );
}

export function overrideTreatmentSlot(projectId, chapterId, slotKey, generationId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/treatments/${encodeURIComponent(
      chapterId
    )}/slots/${encodeURIComponent(slotKey)}`,
    { method: 'POST', body: JSON.stringify({ generationId }) }
  );
}

export function generateTreatmentHero(projectId, chapterId, body = {}) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/treatments/${encodeURIComponent(chapterId)}/hero`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export function generateTreatmentEraText(projectId, chapterId, body = {}) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/treatments/${encodeURIComponent(chapterId)}/era-text`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export function generateTreatmentSheet(projectId, chapterId, body = {}) {
  return jsonFetch(
    longRequestUrl(
      `/api/projects/${encodeURIComponent(projectId)}/treatments/${encodeURIComponent(chapterId)}/sheet`
    ),
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export function buildTreatmentCombined(projectId, chapterId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/treatments/${encodeURIComponent(chapterId)}/build`,
    { method: 'POST', body: '{}' }
  );
}

export function lockTreatmentLayout(projectId, chapterId, locked = true) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/treatments/${encodeURIComponent(chapterId)}/lock`,
    { method: 'POST', body: JSON.stringify({ locked }) }
  );
}

export function exportTreatment(projectId, chapterId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/treatments/${encodeURIComponent(chapterId)}/export`,
    { method: 'POST', body: '{}' }
  );
}

export function listCharacters(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/characters`);
}

export function addCharacter(projectId, body) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/characters`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Merge an override patch into structure.expressionCatalog (project-versioned). */
export function updateExpressionCatalog(projectId, patch) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/expression-catalog`, {
    method: 'PATCH',
    body: JSON.stringify(patch || {}),
  });
}

export function updateCharacter(projectId, itemId, body) {
  return jsonFetch(structureItemPath(projectId, 'characters', itemId), {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function archiveCharacter(projectId, itemId) {
  return jsonFetch(structureItemPath(projectId, 'characters', itemId, 'archive'), {
    method: 'POST',
    body: '{}',
  });
}

export function restoreCharacter(projectId, itemId) {
  return jsonFetch(structureItemPath(projectId, 'characters', itemId, 'restore'), {
    method: 'POST',
    body: '{}',
  });
}

export function deleteCharacter(projectId, itemId) {
  return jsonFetch(structureItemPath(projectId, 'characters', itemId), { method: 'DELETE' });
}

export function listOther(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/other`);
}

export function addOther(projectId, body) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/other`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateOther(projectId, itemId, body) {
  return jsonFetch(structureItemPath(projectId, 'other', itemId), {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function archiveOther(projectId, itemId) {
  return jsonFetch(structureItemPath(projectId, 'other', itemId, 'archive'), {
    method: 'POST',
    body: '{}',
  });
}

export function restoreOther(projectId, itemId) {
  return jsonFetch(structureItemPath(projectId, 'other', itemId, 'restore'), {
    method: 'POST',
    body: '{}',
  });
}

export function deleteOther(projectId, itemId) {
  return jsonFetch(structureItemPath(projectId, 'other', itemId), { method: 'DELETE' });
}

export function listChapters(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/chapters`);
}

export function addChapter(projectId, body) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/chapters`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateChapter(projectId, chapterId, body) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/chapters/${encodeURIComponent(chapterId)}`,
    { method: 'PATCH', body: JSON.stringify(body) }
  );
}

export function archiveChapter(projectId, chapterId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/chapters/${encodeURIComponent(chapterId)}/archive`,
    { method: 'POST', body: '{}' }
  );
}

export function restoreChapter(projectId, chapterId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/chapters/${encodeURIComponent(chapterId)}/restore`,
    { method: 'POST', body: '{}' }
  );
}

export function deleteChapter(projectId, chapterId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/chapters/${encodeURIComponent(chapterId)}`,
    { method: 'DELETE' }
  );
}

export function listDocumentSections(projectId, documentId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}/sections`
  );
}

/** Seed chapter treatment/prompt from a Project Document (.md), optional ## section title. */
export function seedChapterFromDocument(projectId, chapterId, body) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/chapters/${encodeURIComponent(chapterId)}/seed-from-document`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export function listPages(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/pages`);
}

export function addPage(projectId, chapterId, body) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/chapters/${encodeURIComponent(chapterId)}/pages`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export function updatePage(projectId, pageId, body) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/pages/${encodeURIComponent(pageId)}`,
    { method: 'PATCH', body: JSON.stringify(body) }
  );
}

export function archivePage(projectId, pageId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/pages/${encodeURIComponent(pageId)}/archive`,
    { method: 'POST', body: '{}' }
  );
}

export function restorePage(projectId, pageId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/pages/${encodeURIComponent(pageId)}/restore`,
    { method: 'POST', body: '{}' }
  );
}

export function deletePage(projectId, pageId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/pages/${encodeURIComponent(pageId)}`,
    { method: 'DELETE' }
  );
}

export function finalizePage(projectId, pageId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/pages/${encodeURIComponent(pageId)}/finalize`,
    { method: 'POST', body: '{}' }
  );
}

export function listProviders() {
  return jsonFetch('/api/providers');
}

export function getProviderSettings() {
  return jsonFetch('/api/admin/provider-settings');
}

/** Update server-only provider settings. Pass apiKey to set; "" to clear; omit to keep. */
export function updateProviderSettings(body) {
  return jsonFetch('/api/admin/provider-settings', {
    method: 'PATCH',
    body: JSON.stringify(body || {}),
  });
}

/**
 * Generate an illustration attempt.
 * Body may include: chapterId, pageId, sceneId, providerId,
 * plus optional override, characterId, otherId, pose, target
 * ('character' | 'other' | 'page'), castOverridePrefixes.
 */
export async function generateScene(projectId, body) {
  let data;
  try {
    data = await jsonFetch(longRequestUrl(`/api/projects/${encodeURIComponent(projectId)}/generate`), {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  } catch (e) {
    if (e.data?.generation) data = e.data;
    else throw e;
  }
  if (data?.generation && !data.generation.error && data.generation.output) {
    try {
      data.generation = await persistGenerationToCloud(projectId, data.generation);
      data.cloudSynced = true;
    } catch (e) {
      data.cloudSynced = false;
      data.cloudWarning = e.message;
    }
  }
  return data;
}

/** Alias — same as generateScene (accepts override / characterId / otherId / pose / target). */
export const generateIllustration = generateScene;

/** Rebuild project structure from the uploaded reference pack documents.
 *  Not called from the UI. POST /api/projects/:id/pack-rebuild remains on the server.
 */
export function rebuildPack(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/pack-rebuild`, {
    method: 'POST',
    body: '{}',
  });
}

/** Export a locked generation as a 300dpi print artifact. */
export function exportGeneration(projectId, generationId) {
  return jsonFetch(
    longRequestUrl(`/api/projects/${encodeURIComponent(projectId)}/generations/${encodeURIComponent(generationId)}/export-300`),
    { method: 'POST', body: '{}' }
  );
}

/** Save a project file into the browser Downloads folder. */
export async function downloadContentFile(url, filename) {
  const res = await fetch(apiUrl(url), {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error('Could not download the 300dpi file');
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename || 'export-300dpi.png';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

/** Download an already-produced 300dpi file. Live site reads Storage. */
export async function downloadExportFile(projectId, export300, filename) {
  const name = filename || String(export300?.path || 'export-300dpi.png').split('/').pop();
  if (shouldPreferCloudCatalog()) {
    await downloadStorageFile(storagePathForExport(projectId, export300), name);
    return;
  }
  const url = export300?.contentUrl || `/content/projects/${projectId}/${export300.path}`;
  await downloadContentFile(url, name);
}

export async function listGenerations(projectId) {
  if (shouldPreferCloudCatalog()) {
    const generations = await listCloudGenerations(projectId);
    return { generations, source: 'firebase' };
  }
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/generations`);
}

export function getGeneration(projectId, generationId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/generations/${encodeURIComponent(generationId)}`
  );
}

/** Approve/lock a generation as a continuity reference (disk + Firebase). */
export async function approveGeneration(projectId, generationId, body = {}) {
  const data = await jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/generations/${encodeURIComponent(generationId)}/approve`,
    { method: 'POST', body: JSON.stringify(body) }
  );
  try {
    await persistApprovedReferenceToCloud(projectId, {
      reference: data.reference,
      generation: data.generation,
    });
    data.cloudSynced = true;
  } catch (e) {
    data.cloudSynced = false;
    data.cloudWarning = e.message;
  }
  return data;
}

/** Unlock a locked generation (drops bible ref; image stays for delete/re-approve). */
export async function unlockGeneration(projectId, generationId, { approvedReferenceId } = {}) {
  const data = await jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/generations/${encodeURIComponent(generationId)}/unlock`,
    { method: 'POST', body: '{}' }
  );
  const refId = approvedReferenceId || data.removedReferenceId || null;
  if (refId) {
    try {
      await deleteCloudReference(projectId, refId);
      data.cloudSynced = true;
    } catch (e) {
      data.cloudSynced = false;
      data.cloudWarning = e.message;
    }
  }
  return data;
}

/** Hide a generation from its working list, or restore it. The file stays. */
export async function setGenerationArchived(projectId, generationId, archived) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/generations/${encodeURIComponent(generationId)}/archive`,
    { method: 'POST', body: JSON.stringify({ archived }) }
  );
}

/** Delete a generation attempt (local disk + best-effort Firebase). Must be unlocked first. */
export async function deleteGeneration(projectId, generationId, { approvedReferenceId } = {}) {
  const data = await jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/generations/${encodeURIComponent(generationId)}`,
    { method: 'DELETE' }
  );
  try {
    await deleteCloudGeneration(projectId, generationId, {
      approvedReferenceId: approvedReferenceId || data.removedReferenceId || null,
    });
    data.cloudSynced = true;
  } catch (e) {
    if (e?.code === 'AUTH_REQUIRED') {
      data.cloudSynced = false;
      data.cloudWarning = e.message;
    } else {
      data.cloudSynced = false;
      data.cloudWarning = e.message;
    }
  }
  return data;
}

export function listReferences(projectId) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/references`);
}

export function addReference(projectId, body) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/references`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function uploadCharacterBaseReference(projectId, characterId, body) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/characters/${encodeURIComponent(characterId)}/base-reference`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export function clearCharacterBaseReference(projectId, characterId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/characters/${encodeURIComponent(characterId)}/base-reference`,
    { method: 'DELETE' }
  );
}

export function uploadOtherBaseReference(projectId, otherId, body) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/other/${encodeURIComponent(otherId)}/base-reference`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export function clearOtherBaseReference(projectId, otherId) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/other/${encodeURIComponent(otherId)}/base-reference`,
    { method: 'DELETE' }
  );
}

export function addPluginGuide(projectId, body) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/guides`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function playCharacterAnimation(projectId, body) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/animation/character`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function previewCharacterAnimation(projectId, body) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/animation/preview`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function playSceneAnimation(projectId, body) {
  return jsonFetch(`/api/projects/${encodeURIComponent(projectId)}/animation/scene`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function exportSocialClip(projectId, sceneId, body) {
  return jsonFetch(
    `/api/projects/${encodeURIComponent(projectId)}/animation/scenes/${encodeURIComponent(sceneId)}/clip`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}
